import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { useMyRestaurants } from '../hooks/useMyRestaurants';

interface Item {
  id: string;
  name_snapshot: string;
  quantity: number;
  notes: string | null;
  tableLabel: string;
}

function mapItems(data: unknown): Item[] {
  return (data as Array<Record<string, unknown>>).map((it) => {
    const tab = it.tabs as { table_sessions?: { tables?: { label?: string } } } | null;
    return {
      id: it.id as string,
      name_snapshot: it.name_snapshot as string,
      quantity: it.quantity as number,
      notes: (it.notes as string | null) ?? null,
      tableLabel: tab?.table_sessions?.tables?.label ?? '—'
    };
  });
}

export function FloorPage() {
  const { t } = useTranslation();
  const { restaurants, loading } = useMyRestaurants();
  const restaurant = restaurants[0];

  const [ready, setReady] = useState<Item[]>([]);
  const [pending, setPending] = useState<Item[]>([]);
  const [rejected, setRejected] = useState<Item[]>([]);
  const [soundOn, setSoundOn] = useState(true);
  const audioRef = useRef<AudioContext | null>(null);
  const sel = 'id, name_snapshot, quantity, notes, tabs(table_sessions(tables(label)))';

  const reload = useCallback(async () => {
    if (!restaurant) return;
    const base = () =>
      supabase.from('order_items').select(sel).eq('restaurant_id', restaurant.id);
    const { data: r } = await base().eq('kitchen_status', 'ready').order('created_at');
    const { data: q } = await base().eq('kitchen_status', 'queued').order('created_at');
    const { data: x } = await base().eq('kitchen_status', 'cancelled').order('updated_at', { ascending: false }).limit(8);
    if (r) setReady(mapItems(r));
    if (q) setPending(mapItems(q));
    if (x) setRejected(mapItems(x));
  }, [restaurant]);

  useEffect(() => {
    void reload();
    if (!restaurant) return;
    const ch = supabase
      .channel(`floor-${restaurant.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'order_items', filter: `restaurant_id=eq.${restaurant.id}` }, () => void reload())
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [restaurant, reload]);

  // sino contínuo enquanto houver item pronto pra entregar
  useEffect(() => {
    if (!soundOn || ready.length === 0) return;
    const beep = () => {
      try {
        if (!audioRef.current) audioRef.current = new AudioContext();
        const ctx = audioRef.current;
        void ctx.resume();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = 880;
        gain.gain.value = 0.15;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + 0.25);
      } catch { /* autoplay bloqueado até interação */ }
    };
    beep();
    const id = window.setInterval(beep, 1800);
    return () => window.clearInterval(id);
  }, [soundOn, ready.length]);

  async function deliver(id: string) {
    await supabase.from('order_items').update({ kitchen_status: 'served' }).eq('id', id);
    void reload();
  }

  if (loading || !restaurant) return <div className="center-note">{t('common.loading')}</div>;

  return (
    <div className="page page--wide">
      <div className="page__head-row">
        <h1 className="page__title">{t('floor.title')}</h1>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setSoundOn((s) => !s)}>
          {soundOn ? t('floor.sound_on') : t('floor.sound_off')}
        </button>
      </div>

      <h2 className="section-title">{t('floor.ready')} <span className="tag">{ready.length}</span></h2>
      {ready.length === 0 ? (
        <p className="muted-note">{t('floor.none_ready')}</p>
      ) : (
        <div className="order-grid">
          {ready.map((it) => (
            <div key={it.id} className="order-card order-card--alert">
              <div className="order-card__head">
                <strong>{t('floor.table')} {it.tableLabel}</strong>
              </div>
              <div className="order-card__items">{it.quantity}× {it.name_snapshot}{it.notes && <em className="tag"> — {it.notes}</em>}</div>
              <button type="button" className="btn btn--primary btn--sm btn--block" onClick={() => void deliver(it.id)}>
                {t('floor.deliver')}
              </button>
            </div>
          ))}
        </div>
      )}

      <h2 className="section-title">{t('floor.new_orders')} <span className="tag">{pending.length}</span></h2>
      <ul className="list">
        {pending.map((it) => (
          <li key={it.id} className="list__row">
            <span>{t('floor.table')} {it.tableLabel} · {it.quantity}× {it.name_snapshot}</span>
          </li>
        ))}
        {pending.length === 0 && <li className="list__row muted-note">—</li>}
      </ul>

      {rejected.length > 0 && (
        <>
          <h2 className="section-title">{t('floor.rejected')}</h2>
          <ul className="list">
            {rejected.map((it) => (
              <li key={it.id} className="list__row">
                <span className="dimmed">{t('floor.table')} {it.tableLabel} · {it.quantity}× {it.name_snapshot}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
